"use client";

import { MessageChannel, ProcessingJobType } from "@prisma/client";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Components } from "@/app/(components)/_components/Components";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import MessageSequence from "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/MessageSequence/MessageSequence";
import { suggestedNextDelay } from "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/MessageSequence/MessageSequence.logic";
import ObjectiveEmpty from "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/ObjectiveEmpty/ObjectiveEmpty";
import SendingGuideCard from "@/app/(components)/specifics/tools/MessagesContent/components/Sequences/components/SendingGuideCard";
import {
  useCanEditContent,
  useCanManageWorkspace,
  useCanRunPaidActions,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { useInitialSelection } from "@/app/(components)/specifics/wrappers/SelectionProvider/SelectionProvider";
import { deleteSequenceObjective } from "@/app/actions/messages-content/objectives/action";
import {
  deleteMessageSequenceStep,
  deleteMessageSequenceVariant,
  getMessageSequence,
} from "@/app/actions/messages-content/steps/action";
import {
  buildMessageSequenceQueryKey,
  useFetchMessageSequence,
} from "@/app/hooks/fetchs/messages-content/useFetchMessageSequence";
import { useFetchMessageSequenceSummary } from "@/app/hooks/fetchs/messages-content/useFetchMessageSequenceSummary";
import {
  buildSequenceObjectivesQueryKey,
  useFetchSequenceObjectives,
} from "@/app/hooks/fetchs/messages-content/useFetchSequenceObjectives";
import { useFetchRunningJobs } from "@/app/hooks/fetchs/user/useFetchRunningJobs";
import { useCreditGuard } from "@/app/hooks/useCreditGuard";
import { useGeneratingUntilFetched } from "@/app/hooks/useGeneratingUntilFetched";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import {
  MessageSequenceJobMetadata,
  MessageSequenceVariantJobMetadata,
} from "@/app/types/job/ProcessingJob";
import {
  MessageSequenceStepData,
  MessageSequenceStepVariantData,
} from "@/app/types/messages-content/MessageSequenceData";
import { SequenceObjectiveData } from "@/app/types/messages-content/SequenceObjectiveData";
import { UCookie } from "@/app/utils/UCookie";
import { UMessageChannel } from "@/app/utils/UMessageChannel";
import { ProcessingPrice } from "@/app/vars/Credits";
import { Limits } from "@/app/vars/Limits";
import { CookieKey, WORKSPACE_COOKIE_MAX_AGE } from "@/app/vars/Storage";
import styles from "./Sequences.module.css";

const GENERATE_COST = ProcessingPrice[ProcessingJobType.MESSAGE_SEQUENCE];

type Props = DefaultProps & {
  avatarId?: number;
  isConnected: boolean;
};

export default function Sequences(props: Props) {
  const T = useT();
  const { params, avatarId, isConnected } = props;

  const queryClient = useQueryClient();
  const guardCredits = useCreditGuard(params);
  const canRunPaidActions = useCanRunPaidActions();
  const canEditContent = useCanEditContent();
  const canManageWorkspace = useCanManageWorkspace();

  const initialSelection = useInitialSelection();

  const [selectedObjectiveId, setSelectedObjectiveId] = useState<string>(
    initialSelection.objectiveId ? String(initialSelection.objectiveId) : "",
  );
  const [channel, setChannel] = useState<MessageChannel>(initialSelection.channel);

  const { data: fetchedObjectives } = useFetchSequenceObjectives({
    avatarId,
    enabled: isConnected && !!avatarId,
  });

  const objectives = useMemo(() => fetchedObjectives ?? [], [fetchedObjectives]);
  const objectivesLoaded = fetchedObjectives !== undefined;
  const objectiveIdNum = selectedObjectiveId ? parseInt(selectedObjectiveId, 10) : undefined;
  const currentObjective = objectives.find((o) => o.id === objectiveIdNum);

  const {
    data: sequence,
    isLoading: isSequenceLoading,
    isFetching: isSequenceFetching,
  } = useFetchMessageSequence({
    objectiveId: objectiveIdNum,
    channel,
    enabled: isConnected && !!objectiveIdNum,
  });

  const steps = useMemo(() => sequence?.steps ?? [], [sequence]);

  const { data: sequenceSummary } = useFetchMessageSequenceSummary({
    objectiveId: objectiveIdNum,
    channel,
    enabled: isConnected && !!objectiveIdNum,
  });

  const { data: runningJobs } = useFetchRunningJobs({ enabled: isConnected });

  const hasSequenceJob = useMemo(() => {
    if (!runningJobs || !objectiveIdNum) return false;
    return runningJobs.some((job) => {
      if (job.type !== ProcessingJobType.MESSAGE_SEQUENCE) return false;
      const metadata = job.metadata as MessageSequenceJobMetadata | null;
      return metadata?.objectiveId === objectiveIdNum && metadata?.channel === channel;
    });
  }, [runningJobs, objectiveIdNum, channel]);

  const isGenerating = useGeneratingUntilFetched(hasSequenceJob, isSequenceFetching);

  const regeneratingVariantIds = useMemo(() => {
    if (!runningJobs) return [];
    return runningJobs
      .filter((job) => job.type === ProcessingJobType.MESSAGE_SEQUENCE_STEP)
      .map((job) => (job.metadata as MessageSequenceVariantJobMetadata | null)?.variantId)
      .filter((id): id is number => id !== undefined);
  }, [runningJobs]);

  useEffect(() => {
    UMessageChannel.store(channel);
  }, [channel]);

  useEffect(() => {
    if (!objectivesLoaded) {
      return;
    }
    if (objectives.length === 0) {
      setSelectedObjectiveId("");
      return;
    }
    if (selectedObjectiveId && objectives.some((o) => o.id.toString() === selectedObjectiveId)) {
      return;
    }
    const stored = UCookie.read(CookieKey.MESSAGES_CONTENT_SELECTED_OBJECTIVE);
    const next =
      stored && objectives.some((o) => o.id.toString() === stored)
        ? stored
        : objectives[0].id.toString();
    setSelectedObjectiveId(next);
  }, [objectivesLoaded, objectives, selectedObjectiveId]);

  useEffect(() => {
    if (selectedObjectiveId) {
      UCookie.write(
        CookieKey.MESSAGES_CONTENT_SELECTED_OBJECTIVE,
        selectedObjectiveId,
        WORKSPACE_COOKIE_MAX_AGE,
      );
    }
  }, [selectedObjectiveId]);

  const openObjectiveForm = (objective?: SequenceObjectiveData, initialLabel?: string) => {
    if (!avatarId) {
      return;
    }

    const applyObjectives = (next: SequenceObjectiveData[], savedId?: number) => {
      queryClient.setQueryData(buildSequenceObjectivesQueryKey(avatarId), next);
      const kept = savedId ?? next.find((o) => !objectives.some((old) => old.id === o.id))?.id;
      if (kept) {
        setSelectedObjectiveId(String(kept));
      }
    };

    ModalStore.getState().openModal(
      Components.SequenceObjectiveForm,
      T(
        objective
          ? TKey.MESSAGES_CONTENT_OBJECTIVE_EDIT_TITLE
          : TKey.MESSAGES_CONTENT_OBJECTIVE_ADD_TITLE,
      ) as string,
      {
        params,
        avatarId,
        objective,
        initialLabel,
        takenLabels: objectives.map((o) => o.label),
        onSaved: applyObjectives,
      },
      ModalType.MEDIUM,
    );
  };

  const handleDeleteObjective = (objective: SequenceObjectiveData) => {
    if (!avatarId) {
      return;
    }

    ModalStore.getState().openConfirmDialog(
      params,
      TKey.MESSAGES_CONTENT_OBJECTIVE_DELETE_TITLE,
      TKey.MESSAGES_CONTENT_OBJECTIVE_DELETE_TEXT,
      TKey.MESSAGES_CONTENT_OBJECTIVE_DELETE_CONFIRM,
      async () => {
        const next = await deleteSequenceObjective(objective.id);
        queryClient.setQueryData(buildSequenceObjectivesQueryKey(avatarId), next);
        setSelectedObjectiveId(next[0] ? String(next[0].id) : "");
      },
    );
  };

  const openStepForm = (step?: MessageSequenceStepData) => {
    if (!objectiveIdNum) {
      return;
    }
    ModalStore.getState().openModal(
      Components.SequenceStepForm,
      T(
        step
          ? TKey.MESSAGES_CONTENT_STEP_MODAL_EDIT_TITLE
          : TKey.MESSAGES_CONTENT_STEP_MODAL_ADD_TITLE,
      ) as string,
      {
        params,
        objectiveId: objectiveIdNum,
        channel,
        step,
        suggestedDelay: step?.delay ?? suggestedNextDelay(steps),
        otherSteps: steps.filter((s) => s.id !== step?.id),
      },
      ModalType.MEDIUM,
    );
  };

  const openRegenerate = (
    step: MessageSequenceStepData,
    variant: MessageSequenceStepVariantData,
  ) => {
    if (!objectiveIdNum) {
      return;
    }
    ModalStore.getState().openModal(
      Components.SequenceStepRegenerate,
      T(TKey.MESSAGES_CONTENT_REGENERATE_STEP_MODAL_TITLE) as string,
      { params, objectiveId: objectiveIdNum, channel, step, variant },
      ModalType.SMALL,
    );
  };

  const openVariantForm = (
    step: MessageSequenceStepData,
    variant?: MessageSequenceStepVariantData,
  ) => {
    if (!objectiveIdNum) {
      return;
    }
    ModalStore.getState().openModal(
      Components.SequenceVariantForm,
      T(
        variant
          ? TKey.MESSAGES_CONTENT_VARIANT_MODAL_EDIT_TITLE
          : TKey.MESSAGES_CONTENT_VARIANT_MODAL_ADD_TITLE,
      ) as string,
      { params, objectiveId: objectiveIdNum, channel, stepId: step.id, variant },
      ModalType.MEDIUM,
    );
  };

  const handleDeleteVariant = (variant: MessageSequenceStepVariantData) => {
    ModalStore.getState().openConfirmDialog(
      params,
      TKey.MESSAGES_CONTENT_DELETE_VARIANT_TITLE,
      TKey.MESSAGES_CONTENT_DELETE_VARIANT_TEXT,
      TKey.MESSAGES_CONTENT_DELETE_VARIANT_CONFIRM,
      async () => {
        await deleteMessageSequenceVariant(variant.id);
        queryClient.invalidateQueries({
          queryKey: buildMessageSequenceQueryKey(objectiveIdNum, channel),
        });
      },
    );
  };

  const handleDeleteStep = (step: MessageSequenceStepData) => {
    ModalStore.getState().openConfirmDialog(
      params,
      TKey.MESSAGES_CONTENT_DELETE_STEP_TITLE,
      TKey.MESSAGES_CONTENT_DELETE_STEP_TEXT,
      TKey.MESSAGES_CONTENT_DELETE_STEP_CONFIRM,
      async () => {
        await deleteMessageSequenceStep(step.id);
        queryClient.invalidateQueries({
          queryKey: buildMessageSequenceQueryKey(objectiveIdNum, channel),
        });
      },
    );
  };

  const handleGenerate = async () => {
    if (!objectiveIdNum || !currentObjective || !guardCredits(GENERATE_COST)) {
      return;
    }

    const loaded =
      sequence ??
      (await queryClient.ensureQueryData({
        queryKey: buildMessageSequenceQueryKey(objectiveIdNum, channel),
        queryFn: () => getMessageSequence(objectiveIdNum, channel),
      }));

    const loadedSteps = loaded?.steps ?? [];
    const isRegeneration = loadedSteps.length > 0;

    ModalStore.getState().openModal(
      Components.SequenceGenerate,
      T(
        isRegeneration
          ? TKey.MESSAGES_CONTENT_REGENERATE_MODAL_TITLE
          : TKey.MESSAGES_CONTENT_GENERATE_MODAL_TITLE,
      ) as string,
      {
        params,
        objectiveId: objectiveIdNum,
        objectiveLabel: currentObjective.label,
        channel,
        isRegeneration,
        currentRules: loaded?.aiRules ?? null,
        currentVariantsCount: loadedSteps[0]?.variants.length ?? Limits.MAX_SEQUENCE_STEP_VARIANTS,
      },
      ModalType.SMALL,
    );
  };

  if (!isConnected) {
    const openSignUp = () => ModalStore.getState().openSignUp(params);
    return (
      <div className={styles.section}>
        <MessageSequence
          params={params}
          channel={channel}
          steps={[]}
          isPlaceholder={true}
          onChangeChannel={setChannel}
          onChangeObjective={openSignUp}
          onAddStep={openSignUp}
          onEditStep={openSignUp}
          onDeleteStep={openSignUp}
          onAddVariant={openSignUp}
          onEditVariant={openSignUp}
          onRegenerateVariant={openSignUp}
          onDeleteVariant={openSignUp}
          onGenerate={openSignUp}
        />

        <SendingGuideCard params={params} isConnected={false} />
      </div>
    );
  }

  return (
    <div className={styles.section}>
      {!objectivesLoaded ? (
        <div className={styles.loaderContainer}>
          <PointsLoader color="var(--color-green-3)" delay={Limits.PROSPECT_SEARCH_LOADER_DELAY} />
        </div>
      ) : objectives.length === 0 ? (
        <ObjectiveEmpty
          onCreate={canEditContent ? (label) => openObjectiveForm(undefined, label) : undefined}
        />
      ) : (
        <MessageSequence
          params={params}
          channel={channel}
          steps={steps}
          isLoading={isSequenceLoading || (isSequenceFetching && steps.length === 0)}
          isGenerating={isGenerating}
          regeneratingVariantIds={regeneratingVariantIds}
          hasStepsHint={sequenceSummary?.hasSteps ?? false}
          needUpdate={sequenceSummary?.needUpdate ?? false}
          avatarUpdatedAt={sequenceSummary?.avatarUpdatedAt ?? null}
          onChangeChannel={setChannel}
          objectives={objectives}
          objectiveId={objectiveIdNum}
          onChangeObjective={(id) => setSelectedObjectiveId(String(id))}
          onEditObjective={canEditContent ? openObjectiveForm : undefined}
          onDeleteObjective={canManageWorkspace ? handleDeleteObjective : undefined}
          onCreateObjective={
            canEditContent && objectives.length < Limits.MAX_SEQUENCE_OBJECTIVES
              ? () => openObjectiveForm()
              : undefined
          }
          onAddStep={canEditContent ? () => openStepForm() : undefined}
          onEditStep={canEditContent ? openStepForm : undefined}
          onDeleteStep={canManageWorkspace ? handleDeleteStep : undefined}
          onAddVariant={canEditContent ? openVariantForm : undefined}
          onEditVariant={canEditContent ? openVariantForm : undefined}
          onRegenerateVariant={canRunPaidActions ? openRegenerate : undefined}
          onDeleteVariant={canManageWorkspace ? handleDeleteVariant : undefined}
          onGenerate={canRunPaidActions ? handleGenerate : undefined}
        />
      )}

      {objectives.length > 0 && <SendingGuideCard params={params} isConnected={true} />}
    </div>
  );
}
